import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getStorePreviewUrl } from "@/utils/storeUtils";
import { Check, X, Clock, Mail, Layers, Search, Trash2, AlertCircle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = (process.env.REACT_APP_FRONTEND_URL || window.location.origin).replace(/\/$/, '');

/*
 * Where the application came from. Both fields are pinned server-side by the
 * route that accepted it (see onboarding.routes.js), so neither can be spoofed
 * by the applicant — which is what makes them safe to branch the approval UI on.
 * `source` is checked first because it is the field the marketing site and the
 * app both set explicitly; `module_type` is the fallback for older rows.
 */
const isInfinityRequest = (request) =>
  request?.source === "infinitytouch" || request?.module_type === "infinity_touch";

const isDistributorRequest = (request) => request?.partner_type === "distributor";

export default function AdminRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [customSubdomain, setCustomSubdomain] = useState("");
  const [distributors, setDistributors] = useState([]);
  const [selectedDistributor, setSelectedDistributor] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    description: "",
    onConfirm: () => { },
    isLoading: false
  });

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    fetchRequests();
  }, [page, limit, searchTerm]);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/onboarding/requests`, {
        params: { page, limit, keyword: searchTerm }
      });

      if (response.data.data && response.data.pagination) {
        setRequests(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
        setTotalItems(response.data.pagination.total);
      } else {
        setRequests(response.data);
        setTotalPages(1);
        setTotalItems(response.data.length);
      }
    } catch (error) {
      toast.error("Failed to fetch requests");
    } finally {
      setLoading(false);
    }
  };

  const fetchDistributors = async () => {
    try {
      const response = await axios.get(`${API}/admin/distributors`);
      setDistributors(response.data || []);
    } catch (error) {
      console.error("Failed to fetch distributors", error);
    }
  };

  const handleApproveClick = (request) => {
    setSelectedRequest(request);
    /*
     * Pre-select whoever referred the applicant, rather than starting blank.
     *
     * The distributor link drives commission, and a blank default meant the
     * commonest outcome of a busy approval was an unlinked funeral home. With
     * the referral pre-selected, leaving it alone credits the right distributor
     * and choosing "No Distributor" becomes a deliberate act. The admin can
     * still override, which is what the dropdown is for.
     */
    setSelectedDistributor(request.distributor_id || "");
    // Derive a clean slug suggestion from business_website or business_name
    let suggestion = request.business_website || request.preferred_subdomain || request.business_name || "";
    suggestion = suggestion
      .toLowerCase()
      .replace(/^(https?:\/\/)?(www\.)?/, '')
      .split('/')[0]
      .split('.')[0]
      .replace(/[^a-z0-9-]/g, '');

    setCustomSubdomain(suggestion);
    setApprovalDialogOpen(true);
    fetchDistributors();
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    /*
     * The subdomain is only mandatory for an eDisplayroom application, because
     * that is the only one that produces a storefront anybody visits by name.
     * An Infinity Touch signup has no storefront — the server derives a slug
     * from the business name purely to satisfy the unique, required field on the
     * store — so demanding one here was making the admin invent a URL that
     * nothing ever resolves.
     */
    if (!isInfinityRequest(selectedRequest) && (!customSubdomain || customSubdomain.length < 2)) {
      return toast.error("Please enter a valid store slug (at least 2 characters)");
    }

    setProcessing(selectedRequest.request_id);
    try {
      const { data } = await axios.post(`${API}/onboarding/approve/${selectedRequest.request_id}`, {
        subdomain: customSubdomain,
        distributor_id: selectedDistributor || null
      });
      toast.success(
        data?.partner_type === "distributor"
          ? `Distributor created. Referral code ${data.referral_code} has been emailed to them.`
          : "Request approved! Store created and welcome email sent."
      );
      setApprovalDialogOpen(false);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.detail || "Failed to approve request");
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (requestId) => {
    setConfirmDialog({
      isOpen: true,
      title: "Reject Request?",
      description: "Are you sure you want to reject this request? This action will notify the applicant.",
      onConfirm: async () => {
        setProcessing(requestId);
        try {
          await axios.post(`${API}/onboarding/reject/${requestId}`);
          toast.success("Request rejected");
          fetchRequests();
        } catch (error) {
          toast.error("Failed to reject request");
        } finally {
          setProcessing(null);
        }
      }
    });
  };

  const handleDelete = async (requestId) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete Request?",
      description: "Are you sure you want to permanently delete this rejected request? This action cannot be undone.",
      onConfirm: async () => {
        setProcessing(requestId);
        try {
          await axios.delete(`${API}/onboarding/delete/${requestId}`);
          toast.success("Request deleted successfully");
          fetchRequests();
        } catch (error) {
          toast.error(error.response?.data?.message || "Failed to delete request");
        } finally {
          setProcessing(null);
        }
      }
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-requests">
      <div>
        <h2 className="text-2xl font-bold text-slate-900">
          Onboarding Requests
        </h2>
        <p className="text-slate-500">Review and approve new store requests</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by business name, contact, or email..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            className="pl-10"
          />
        </div>
      </div>

      <div className="card-elevated overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold">Business</TableHead>
              <TableHead className="font-semibold">Contact</TableHead>
              <TableHead className="font-semibold">Business Website</TableHead>
              <TableHead className="font-semibold">Source</TableHead>
              <TableHead className="font-semibold">Date</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                  No requests yet
                </TableCell>
              </TableRow>
            ) : (
              requests.map((request) => (
                <TableRow key={request.request_id} className="table-row">
                  <TableCell>
                    <div>
                      <p className="font-medium text-slate-900">{request.business_name}</p>
                      {request.message && (
                        <p className="text-xs text-slate-500 truncate max-w-xs" title={request.message}>
                          {request.message}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-slate-900">{request.contact_name}</p>
                      <p className="text-sm text-slate-500">{request.contact_email}</p>
                      {request.phone && (
                        <p className="text-xs text-slate-400">{request.phone}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {request.business_website ? (
                      <a
                        href={request.business_website.startsWith('http') ? request.business_website : `https://${request.business_website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-indigo-600 hover:underline"
                      >
                        {request.business_website}
                      </a>
                    ) : (
                      <span className="text-slate-400 italic text-sm">Not provided</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {/* Replaces a "Categories" column that read selected_categories —
                        a field that does not exist on the model, so it always said
                        "None". Source distinguishes the two public entry points. */}
                    <div className="flex flex-col gap-1">
                      <span className={`text-xs px-2 py-0.5 rounded w-fit ${
                        request.source === "infinitytouch"
                          ? "bg-sky-100 text-sky-700"
                          : "bg-slate-100 text-slate-600"
                      }`}>
                        {request.source === "infinitytouch" ? "infinitytouch.app" : "edisplayroom.com"}
                      </span>
                      {request.partner_type && (
                        <span className="text-xs text-slate-500">
                          {request.partner_type === "distributor" ? "Distributor" : "Funeral Home"}
                        </span>
                      )}
                      {request.referral_code && (
                        <span className="text-xs text-slate-500" title={`Referral code ${request.referral_code}`}>
                          via {request.referral_distributor_name || request.referral_code}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {formatDate(request.created_at)}
                  </TableCell>
                  <TableCell>
                    <span className={`badge ${request.status === 'pending' ? 'badge-warning' :
                      request.status === 'approved' ? 'badge-success' : 'badge-error'
                      }`}>
                      {request.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                      {request.status === 'approved' && <Check className="w-3 h-3 mr-1" />}
                      {request.status === 'rejected' && <X className="w-3 h-3 mr-1" />}
                      {request.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    {request.status === 'pending' && (
                      <div className="flex items-center justify-end gap-2">
                        {/*
                          * Distributor applications used to be disabled here and
                          * the admin sent to Admin > Distributors to retype the
                          * details by hand, which left the application itself
                          * stranded as pending forever. Approving one now creates
                          * the distributor (and mints its referral code) and closes
                          * the application — see approveDistributorRequest.js.
                          */}
                        <Button
                          size="sm"
                          onClick={() => handleApproveClick(request)}
                          disabled={processing === request.request_id}
                          title={isDistributorRequest(request)
                            ? "Approving creates the distributor account and emails them their referral code."
                            : undefined}
                          className="bg-lime-500 hover:bg-lime-600 text-slate-900"
                          data-testid={`approve-click-${request.request_id}`}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleReject(request.request_id)}
                          disabled={processing === request.request_id}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          data-testid={`reject-request-${request.request_id}`}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    )}
                    {request.status === 'approved' && (
                      <span className="text-sm text-slate-500 flex items-center justify-end gap-1">
                        <Mail className="w-4 h-4" />
                        Email sent
                      </span>
                    )}
                    {request.status === 'rejected' && (
                      <div className="flex items-center justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(request.request_id)}
                          disabled={processing === request.request_id}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8 gap-1"
                          title="Permanently Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span className="text-xs font-semibold">Delete</span>
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      {totalItems > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t">
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <div>
              Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to <span className="font-medium">{Math.min(page * limit, totalItems)}</span> of <span className="font-medium">{totalItems}</span> results
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-600">Rows per page:</span>
              <Select
                value={limit.toString()}
                onValueChange={(val) => {
                  setLimit(Number(val));
                  setPage(1);
                }}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue placeholder={limit.toString()} />
                </SelectTrigger>
                <SelectContent side="top">
                  {[10, 15, 25, 50, 100].map((pageSize) => (
                    <SelectItem key={pageSize} value={pageSize.toString()}>
                      {pageSize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>

            <div className="flex items-center mx-2 space-x-1">
              {Array.from({ length: totalPages }).map((_, i) => {
                const pageNum = i + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= page - 1 && pageNum <= page + 1)
                ) {
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? "default" : "outline"}
                      size="sm"
                      className={`h-8 w-8 p-0 ${page === pageNum ? 'bg-primary hover:bg-primary/90 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                }

                if (pageNum === page - 2 || pageNum === page + 2) {
                  return <span key={pageNum} className="text-slate-400 px-1">...</span>;
                }

                return null;
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {isDistributorRequest(selectedRequest) ? "Approve Distributor Application" : "Approve Reseller Request"}
            </DialogTitle>
            <DialogDescription>
              {isDistributorRequest(selectedRequest)
                ? "Review the details below. Approving creates the distributor account and issues their referral code."
                : "Review the details below and assign a store subdomain for the new reseller."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Full reseller details for quick review */}
            {selectedRequest && (
              <div className="p-4 bg-slate-50 rounded-lg text-sm space-y-2 border">
                <h4 className="font-semibold text-slate-900 mb-2">Reseller Details</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <p className="text-slate-500">Business Name</p>
                  <p className="font-medium text-slate-900">{selectedRequest.business_name}</p>
                  <p className="text-slate-500">Contact Name</p>
                  <p className="font-medium text-slate-900">{selectedRequest.contact_name}</p>
                  <p className="text-slate-500">Email</p>
                  <p className="font-medium text-slate-900">{selectedRequest.contact_email}</p>
                  {selectedRequest.phone && (
                    <>
                      <p className="text-slate-500">Phone</p>
                      <p className="font-medium text-slate-900">{selectedRequest.phone}</p>
                    </>
                  )}
                  <p className="text-slate-500">Website</p>
                  <p className="font-medium text-slate-900">
                    {selectedRequest.business_website ? (
                      <a
                        href={selectedRequest.business_website.startsWith('http') ? selectedRequest.business_website : `https://${selectedRequest.business_website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {selectedRequest.business_website}
                      </a>
                    ) : (
                      <span className="text-slate-400 italic text-sm">Not provided</span>
                    )}
                  </p>
                  {/* selected_categories was read here too, but no such field
                      exists on OnboardingRequest. Replaced with the fields that
                      actually distinguish an application. */}
                  <p className="text-slate-500">Source</p>
                  <p className="font-medium text-slate-900">
                    {selectedRequest.source === "infinitytouch" ? "infinitytouch.app" : "edisplayroom.com"}
                  </p>
                  <p className="text-slate-500">Partner type</p>
                  <p className="font-medium text-slate-900">
                    {selectedRequest.partner_type === "distributor" ? "Distributor" : "Funeral Home"}
                  </p>
                </div>
                {/* Address */}
                {selectedRequest.business_address && (
                  <div className="mt-2 pt-2 border-t border-slate-200">
                    <p className="text-slate-500 text-xs font-semibold mb-1">Business Address</p>
                    <p className="text-slate-700 text-sm">
                      {[selectedRequest.business_address.address, selectedRequest.business_address.city, selectedRequest.business_address.state, selectedRequest.business_address.zipcode, selectedRequest.business_address.country].filter(Boolean).join(', ')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/*
              * Distributor applications create a distributor account, not a
              * store, so neither the subdomain nor the "assign a distributor"
              * dropdown means anything for them.
              */}
            {isDistributorRequest(selectedRequest) ? (
              <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-slate-700">
                Approving creates a distributor account for{" "}
                <strong>{selectedRequest?.business_name}</strong>, mints their referral
                code and emails it to <strong>{selectedRequest?.contact_email}</strong>.
                No store is created.
              </div>
            ) : (
              <>
                {/* Subdomain slug input */}
                <div className="space-y-2">
                  <Label htmlFor="subdomain">
                    Store Subdomain
                    {isInfinityRequest(selectedRequest) && (
                      <span className="text-slate-400 text-xs font-normal"> (Optional)</span>
                    )}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="subdomain"
                      value={customSubdomain}
                      onChange={(e) => setCustomSubdomain(e.target.value.trim().toLowerCase().replace(/[^a-z0-9-_]/g, ''))}
                      placeholder="my-store"
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    {isInfinityRequest(selectedRequest)
                      ? "Infinity Touch accounts have no storefront. Leave this blank and a slug is derived from the business name."
                      : <>Store will be available at: <strong className="text-primary">{getStorePreviewUrl(customSubdomain).replace(/^https?:\/\//, '')}</strong></>}
                  </p>
                </div>
              </>
            )}

            {/*
              * What the applicant claimed, shown next to the control that acts on
              * it. The name is a snapshot taken when the code was accepted, so it
              * still reads sensibly if the distributor has since been removed —
              * and distributor_id being empty while a code is present is exactly
              * that case, worth flagging rather than showing a blank dropdown.
              */}
            {selectedRequest?.referral_code && (
              <div className="space-y-1 rounded-md border border-slate-200 bg-slate-50 p-3">
                <Label className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
                  Referred by
                </Label>
                <p className="text-sm text-slate-800">
                  <strong>{selectedRequest.referral_distributor_name || "Unknown distributor"}</strong>
                  <code className="ml-2 font-mono text-xs bg-white border px-1.5 py-0.5 rounded">
                    {selectedRequest.referral_code}
                  </code>
                  {!selectedRequest.distributor_id && (
                    <span className="ml-2 text-xs text-red-600">
                      — no longer a distributor, choose one below or continue without
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Assign Distributor (Optional) — a distributor has no distributor. */}
            {!isDistributorRequest(selectedRequest) && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-blue-500" />
                  Assign Distributor <span className="text-slate-400 text-xs font-normal">(Optional)</span>
                </Label>
                <Select value={selectedDistributor} onValueChange={setSelectedDistributor}>
                  <SelectTrigger>
                    <SelectValue placeholder="No distributor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Distributor</SelectItem>
                    {distributors.map((d) => (
                      <SelectItem key={d.distributor_id} value={d.distributor_id}>
                        {d.company_name || d.name} {d.commission_percent ? `(${d.commission_percent}%)` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleApprove}
              disabled={processing === selectedRequest?.request_id}
              className="bg-primary hover:bg-primary/90"
            >
              {processing === selectedRequest?.request_id ? "Approving..." : "Confirm Approval"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialog.isOpen} onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, isOpen: open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              {confirmDialog.title}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDialog.onConfirm().then(() => {
                  setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                });
              }}
              disabled={processing}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {processing ? "Processing..." : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
